import { ProductType, Product, CouponDiscountType, AppSettings } from "@core/types";
import { StoreApi } from "zustand";
import { CartDataState, CartDataActions, CartDataProductsItem, AddToCartProduct } from "./types";
import { AppMetricaProvider } from "../AppMetrica/AppMetrica";
import { toastController } from "@ionic/core/components";
import { Coupon } from "../coupons/coupon";
import { getMonthOffsetDate } from "./utils";
import i18n from "../../i18n";
import { getProductQuantityStep, isWeightBasedProduct, normalizeProductQuantity } from "../../utils/productQuantity";
import { getAppConfig } from "@core/config";

type StoreApiSetState = StoreApi<CartDataState & CartDataActions>["setState"];
type StoreApiGetState = StoreApi<CartDataState & CartDataActions>["getState"];

const initialCartState: CartDataState = {
  cartSubtotalValue: 0,
  cartTotalValue: 0,
  cartProducts: [],
  cartProductsMap: {},
  soldOutProducts: [],
  unavailableProducts: [],
  coupons: [],
  rewardPoints: 0,
  updatedAt: getMonthOffsetDate(),
};

const roundToStep = (value: number, step: number) => {
  return Math.round(value / step) * step;
};

const getEffectivePrice = (product: {
  price: string;
  discountPrice?: string | null;
  selectedAttribute?: { coefficient?: number } | null;
}): string => {
  const isDiscount =
    product?.discountPrice &&
    product?.discountPrice !== "0" &&
    product?.discountPrice !== "0.00" &&
    product?.discountPrice !== product?.price;

  const basePrice = isDiscount ? product.discountPrice! : product.price;
  const basePriceNum = parseFloat(basePrice);

  if (isNaN(basePriceNum) || !isFinite(basePriceNum) || basePriceNum < 0) {
    console.warn("Invalid base price:", basePrice, "defaulting to 0");
    return "0";
  }

  if (product?.selectedAttribute?.coefficient) {
    const coefficient = Number(product.selectedAttribute.coefficient) || 1;

    if (isNaN(coefficient) || !isFinite(coefficient) || coefficient < 0) {
      console.warn("Invalid coefficient:", coefficient, "defaulting to 1");
      return basePrice;
    }

    const adjustedPrice = Math.round(basePriceNum * coefficient);

    if (isNaN(adjustedPrice) || !isFinite(adjustedPrice) || adjustedPrice < 0) {
      console.warn("Invalid adjusted price:", adjustedPrice, "using base price");
      return basePrice;
    }

    return adjustedPrice.toString();
  }

  return basePrice;
};

const createCartProductItem = (item: AddToCartProduct): CartDataProductsItem => {
  const { isImageInCartStoreEnabled } = getAppConfig();

  const step = getProductQuantityStep({
    product: item,
  });

  const cartItem = {
    quantity: step,
    originalQuantity: step,
    id: item.id,
    type: item.type,
    name: item.name,
    parentId: item.type === ProductType.VARIATION ? item.parentId! : item.id,
    price: getEffectivePrice(item),
    discountPrice: item.discountPrice,
    purchasable: item.purchasable,
    stockQuantity: item.stockQuantity,
    categories: item.categories,
    onSale: item.onSale,
    weight: item.weight ?? "0",
    attributes: item.attributes ?? null,
    images: isImageInCartStoreEnabled ? (item.images ?? []) : [],
    sku: item.sku ?? "",
    portionUnit: item.portionUnit ?? "",
    unitName: item.unitName ?? "шт.",
    selectedAttribute: item.selectedAttribute ?? null,
    parentName: item.parentName ?? "",
  };

  return cartItem;
};

export const addToCartAction =
  (set: StoreApiSetState): CartDataActions["addToCart"] =>
  (item) => {
    if (!item.purchasable) {
      return;
    }

    set((state): CartDataState => {
      const newProductsMap: CartDataState["cartProductsMap"] = {
        ...state.cartProductsMap,
      };

      if (item.stockQuantity < 1) {
        toastController
          .create({
            message: i18n.t("The product is out of stock"),
            duration: 800,
          })
          .then((toast) => toast.present());
        return state;
      }

      if (item.stockQuantity <= newProductsMap[item.id]?.quantity) {
        toastController
          .create({
            message: i18n.t("Product Quantity is Limited!"),
            duration: 800,
          })
          .then((toast) => toast.present());
        return state;
      }

      let roundedSubTotalValue = state.cartSubtotalValue;
      const effectivePrice = getEffectivePrice(item);

      const step = getProductQuantityStep({
        product: newProductsMap[item.id] ?? item,
      });

      if (newProductsMap[item.id]) {
        if (newProductsMap[item.id].quantity + step <= newProductsMap[item.id].stockQuantity) {
          const currentAttribute = newProductsMap[item.id].selectedAttribute;
          const newAttribute = item.selectedAttribute;

          const shouldUpdateAttribute =
            newAttribute &&
            (!currentAttribute ||
              currentAttribute.id !== newAttribute.id ||
              currentAttribute.coefficient !== newAttribute.coefficient);

          if (shouldUpdateAttribute) {
            const oldPrice = parseFloat(newProductsMap[item.id].price);
            const oldQuantity = newProductsMap[item.id].quantity;
            const oldTotal = oldPrice * oldQuantity;

            newProductsMap[item.id].selectedAttribute = item.selectedAttribute;
            newProductsMap[item.id].price = getEffectivePrice(item);

            const newPrice = parseFloat(newProductsMap[item.id].price);
            const newTotal = newPrice * oldQuantity;

            if (isNaN(newTotal) || !isFinite(newTotal) || newTotal < 0) {
              console.warn("Invalid price calculation in addToCart:", { newPrice, oldQuantity, newTotal });
              return state;
            }

            roundedSubTotalValue = Math.round((state.cartSubtotalValue - oldTotal + newTotal) * 100) / 100;
          }

          const nextQuantity = roundToStep(newProductsMap[item.id].quantity + step, step);
          newProductsMap[item.id].originalQuantity = nextQuantity;
          newProductsMap[item.id].quantity = nextQuantity;

          const currentPrice = parseFloat(newProductsMap[item.id].price) * step;
          roundedSubTotalValue = Math.round((roundedSubTotalValue + currentPrice) * 100) / 100;
        } else if (newProductsMap[item.id].stockQuantity > 0) {
          const oldQuantity = newProductsMap[item.id].quantity;
          newProductsMap[item.id].quantity = newProductsMap[item.id].stockQuantity;
          newProductsMap[item.id].originalQuantity = newProductsMap[item.id].stockQuantity;

          const currentPrice = parseFloat(newProductsMap[item.id].price);
          const quantityDiff = newProductsMap[item.id].stockQuantity - oldQuantity;
          roundedSubTotalValue = Math.round((roundedSubTotalValue + currentPrice * quantityDiff) * 100) / 100;
        }
      } else {
        newProductsMap[item.id] = createCartProductItem(item);
        roundedSubTotalValue = Math.round((state.cartSubtotalValue + parseFloat(effectivePrice) * step) * 100) / 100;
      }

      AppMetricaProvider.reportEvent("addProductToCart", newProductsMap[item.id]);

      const updatedAt = new Date();

      return {
        ...state,
        cartSubtotalValue: roundedSubTotalValue,
        cartProducts: Object.values(newProductsMap),
        cartProductsMap: newProductsMap,
        updatedAt,
      };
    });
  };

export const repeatOrderAction =
  (set: StoreApiSetState): CartDataActions["repeatOrder"] =>
  (products, orderLineItems) => {
    set((state): CartDataState => {
      let cartSubtotalValue = state.cartSubtotalValue;
      const cartProductsMap: CartDataState["cartProductsMap"] = { ...state.cartProductsMap };

      const updateCartProduct = (product: CartDataProductsItem, quantity: number) => {
        const newQuantity = (cartProductsMap[product.id]?.quantity || 0) + quantity;

        if (newQuantity <= product.stockQuantity) {
          cartProductsMap[product.id] = {
            ...product,
            quantity: newQuantity,
          };
          cartSubtotalValue += parseFloat(getEffectivePrice(product)) * newQuantity;
        } else {
          cartProductsMap[product.id] = {
            ...product,
            quantity: product.stockQuantity,
          };
          cartSubtotalValue += parseFloat(getEffectivePrice(product)) * newQuantity;
        }
      };

      const handleProduct = (product: Product) => {
        const matchingLineItem = orderLineItems?.find((lineItem) => lineItem.productId === product.id);
        const quantity = matchingLineItem?.quantity || 1;

        const productItem = {
          ...createCartProductItem(product as AddToCartProduct),
          quantity,
          originalQuantity: quantity,
        };

        updateCartProduct(productItem, quantity);
      };

      products.forEach(handleProduct);
      const updatedAt = new Date();

      return {
        ...state,
        cartSubtotalValue,
        cartProducts: Object.values(cartProductsMap),
        cartProductsMap,
        updatedAt,
      };
    });
  };

export const removeFromCartAction =
  (set: StoreApiSetState): CartDataActions["removeFromCart"] =>
  (productId, isAuthenticated) => {
    AppMetricaProvider.reportEvent("removeProductFromCart", { id: productId });

    set((state): CartDataState => {
      const newProductsMap: CartDataState["cartProductsMap"] = {
        ...state.cartProductsMap,
      };

      const product = newProductsMap[productId];
      const step = getProductQuantityStep({
        product: product ?? {},
      });

      if (product && product.quantity > 0) {
        const nextQuantity = roundToStep(product.quantity - step, step);
        product.originalQuantity = nextQuantity;
        product.quantity = nextQuantity;
      } else {
        return state;
      }

      const removedValue = parseFloat(product.price) * step;
      const roundedTotalValue = Math.round((state.cartSubtotalValue - removedValue) * 100) / 100;

      if (product.quantity <= 0) {
        delete newProductsMap[productId];
      }

      const isCartEmpty = Object.keys(newProductsMap).length === 0;
      const updatedAt = isCartEmpty && isAuthenticated ? new Date() : getMonthOffsetDate();

      return {
        ...state,
        cartSubtotalValue: roundedTotalValue,
        cartProducts: Object.values(newProductsMap),
        cartProductsMap: newProductsMap,
        coupons: [],
        rewardPoints: isCartEmpty ? 0 : state.rewardPoints,
        updatedAt,
      };
    });
  };

export const removeAllFromCartAction =
  (set: StoreApiSetState): CartDataActions["removeAllFromCart"] =>
  (productId, isAuthenticated) => {
    AppMetricaProvider.reportEvent("removeAllProductFromCart", { id: productId });

    set((state): CartDataState => {
      const newProductsMap: CartDataState["cartProductsMap"] = {
        ...state.cartProductsMap,
      };

      if (!newProductsMap[productId]) {
        return state;
      }

      const productPrice = parseFloat(newProductsMap[productId].price);
      const productQuantity = newProductsMap[productId].quantity;
      const totalProductValue = productPrice * productQuantity;

      delete newProductsMap[productId];

      const roundedTotalValue = Math.round((state.cartSubtotalValue - totalProductValue) * 100) / 100;

      const isCartEmpty = Object.keys(newProductsMap).length === 0;
      const updatedAt = isCartEmpty && isAuthenticated ? new Date() : getMonthOffsetDate();

      return {
        ...state,
        cartSubtotalValue: roundedTotalValue,
        cartProducts: Object.values(newProductsMap),
        cartProductsMap: newProductsMap,
        coupons: [],
        rewardPoints: isCartEmpty ? 0 : state.rewardPoints,
        updatedAt,
      };
    });
  };

export const replaceCartProductsAction =
  (set: StoreApiSetState): CartDataActions["replaceCartProducts"] =>
  (products) => {
    AppMetricaProvider.reportEvent("replaceCart", {
      products: products.map(({ id, price, name, parentId, stockQuantity, weight }) => ({
        id,
        price,
        name,
        parentId,
        stockQuantity,
        weight,
      })),
    });

    set((state) => {
      if (!products.length) {
        const updatedAt = new Date();
        return {
          ...state,
          cartSubtotalValue: 0,
          cartProducts: [],
          cartProductsMap: {},
          soldOutProducts: [],
          unavailableProducts: [...state.cartProducts],
          updatedAt,
        };
      }

      const newProductsMap: CartDataState["cartProductsMap"] = {};
      const soldOutProducts: CartDataState["soldOutProducts"] = [];
      const unavailableProducts: CartDataState["unavailableProducts"] = [];
      const { isShouldRecalculatePriceOnReplace } = getAppConfig();

      state.cartProducts.forEach((cartProduct) => {
        const updatedProduct = products.find((product) => {
          return product.id === cartProduct.id;
        });

        if (!updatedProduct) {
          unavailableProducts.push(cartProduct);
          return;
        }

        let quantity;

        const cartProductQuantity = cartProduct.quantity;
        const productStockQuantity = updatedProduct.stockQuantity;
        const originalQuantity = cartProduct.originalQuantity ?? cartProductQuantity;

        if (productStockQuantity >= cartProductQuantity) {
          quantity = cartProductQuantity;
        } else {
          quantity = Math.trunc(productStockQuantity);
        }

        if (productStockQuantity < 1) {
          soldOutProducts.push({ ...cartProduct, quantity, stockQuantity: productStockQuantity });
          return;
        }

        const existingPrice = parseFloat(cartProduct.price);
        const newBasePrice = parseFloat(updatedProduct.price);

        const shouldRecalculatePrice =
          isShouldRecalculatePriceOnReplace &&
          (!existingPrice ||
            !isFinite(existingPrice) ||
            existingPrice <= 0 ||
            Math.abs(existingPrice / newBasePrice) > 1000);

        if (shouldRecalculatePrice) {
          const productWithAttribute = {
            ...updatedProduct,
            selectedAttribute: cartProduct.selectedAttribute,
          };

          newProductsMap[updatedProduct.id] = {
            ...createCartProductItem(productWithAttribute as AddToCartProduct),
            quantity,
            originalQuantity,
            unitName: cartProduct.unitName,
            selectedAttribute: cartProduct.selectedAttribute,
            parentName: cartProduct.parentName,
          };
        } else {
          newProductsMap[updatedProduct.id] = {
            ...createCartProductItem(updatedProduct as AddToCartProduct),
            quantity,
            originalQuantity,
            unitName: cartProduct.unitName,
            selectedAttribute: cartProduct.selectedAttribute,
            stockQuantity: updatedProduct.stockQuantity,
            purchasable: updatedProduct.purchasable,
            parentName: cartProduct.parentName,
          };
        }
      });

      const newCartProducts = Object.values(newProductsMap);
      const newCartTotalValue = newCartProducts.reduce((sum, item) => {
        const price = parseFloat(item.price) || 0;
        const quantity = item.quantity || 0;
        const itemTotal = price * quantity;

        if (isNaN(itemTotal) || !isFinite(itemTotal)) {
          console.warn("Invalid item total calculation:", { price, quantity, itemTotal });
          return sum;
        }

        return sum + itemTotal;
      }, 0);

      const updatedAt = new Date();

      return {
        ...state,
        cartSubtotalValue: newCartTotalValue,
        cartProducts: newCartProducts,
        cartProductsMap: newProductsMap,
        soldOutProducts,
        unavailableProducts,
        updatedAt,
      };
    });
  };

export const removeUnavailableProductAction =
  (set: StoreApiSetState): CartDataActions["removeUnavailableProduct"] =>
  (productId: Product["id"]) => {
    const updatedAt = new Date();
    set((state) => ({
      ...state,
      updatedAt,
      unavailableProducts: state.unavailableProducts.filter(
        (unavailableProducts) => unavailableProducts.id !== productId,
      ),
    }));
  };

export const resetCartAction =
  (set: StoreApiSetState): CartDataActions["resetCart"] =>
  (isAuthenticated = false) => {
    AppMetricaProvider.reportEvent("resetCart");
    const updatedAt = isAuthenticated ? new Date() : getMonthOffsetDate();

    set({
      ...initialCartState,
      updatedAt,
    });
  };

export const removeSoldOutProductAction =
  (set: StoreApiSetState): CartDataActions["removeSoldOutProduct"] =>
  (productId: Product["id"]) => {
    const updatedAt = new Date();
    set((state) => ({
      ...state,
      updatedAt,
      soldOutProducts: state.soldOutProducts.filter((soldOutProducts) => soldOutProducts.id !== productId),
    }));
  };

export const clearSoldOutProductsAction =
  (set: StoreApiSetState): CartDataActions["clearSoldOutProducts"] =>
  (isAuthenticated = false) => {
    const updatedAt = isAuthenticated ? new Date() : getMonthOffsetDate();
    set({ ...initialCartState, updatedAt });
  };

export const clearUnavailableProductsAction =
  (set: StoreApiSetState): CartDataActions["clearUnavailableProducts"] =>
  (isAuthenticated = false) => {
    const updatedAt = isAuthenticated ? new Date() : getMonthOffsetDate();
    set((state) => ({
      ...state,
      updatedAt,
      unavailableProducts: [],
    }));
  };

export const applyCouponAction =
  (set: StoreApiSetState, get: StoreApiGetState): CartDataActions["applyCoupon"] =>
  async (coupon) => {
    const { coupons, cartProducts, cartSubtotalValue } = get();
    if (!(await Coupon.validateCouponService(coupon, cartProducts, coupons, cartSubtotalValue))) {
      return;
    }

    set((state) => {
      const newCoupons = [...state.coupons];

      newCoupons.push(coupon);
      return {
        ...state,
        coupons: newCoupons,
      };
    });
  };

export const removeCouponAction =
  (set: StoreApiSetState): CartDataActions["removeCoupon"] =>
  (code: string) => {
    set((state) => {
      const newCoupons = state.coupons.filter((couponItem) => couponItem.code !== code);
      return {
        ...state,
        coupons: newCoupons,
      };
    });
  };

export const addRewardPointsAction =
  (set: StoreApiSetState): CartDataActions["addRewardPoints"] =>
  (points: number) => {
    set((state) => {
      const rewardPoints = state.rewardPoints + points;
      return {
        ...state,
        rewardPoints,
      };
    });
  };

export const resetRewardPointsAction =
  (set: StoreApiSetState): CartDataActions["resetRewardPoints"] =>
  () => {
    set((state) => ({ ...state, rewardPoints: 0 }));
  };

export const getCartTotalAction =
  (set: StoreApiSetState, get: StoreApiGetState): CartDataActions["getCartTotal"] =>
  () => {
    const { cartSubtotalValue, coupons, rewardPoints, cartProducts } = get();

    if (isNaN(cartSubtotalValue) || !isFinite(cartSubtotalValue) || cartSubtotalValue < 0) {
      console.warn("Invalid cart subtotal detected, resetting cart:", cartSubtotalValue);
      set({ ...initialCartState, updatedAt: new Date() });
      return 0;
    }

    if (!coupons.length) {
      const total = cartSubtotalValue - rewardPoints;
      return Math.max(0, total);
    }

    const discount = coupons.reduce((_discount, coupon) => {
      const discountAmount = coupon.discountAmount;
      switch (coupon.discountType) {
        case CouponDiscountType.PERCENT:
          return (_discount += (cartSubtotalValue / 100) * discountAmount);
        case CouponDiscountType.FIXED_CART:
          return (_discount += discountAmount);
        case CouponDiscountType.FIXED_PRODUCT:
          return (_discount += cartProducts.length * discountAmount);
        default:
          return _discount;
      }
    }, 0);

    const total = cartSubtotalValue - discount - rewardPoints;
    return Math.max(0, total);
  };

export const getProductsTotalWeightAction =
  (set: StoreApiSetState, get: StoreApiGetState): CartDataActions["getProductsTotalWeight"] =>
  () => {
    const { cartProducts } = get();
    const totalWeight = cartProducts.reduce(
      (total, product) => (total += parseFloat(product.weight) * product.quantity),
      0,
    );
    return totalWeight;
  };

export const setRewardPointsAction =
  (set: StoreApiSetState): CartDataActions["setRewardPoints"] =>
  (points: number) => {
    const updatedAt = new Date();
    set((state) => ({ ...state, rewardPoints: points, updatedAt }));
  };

export const addMultipleToCartAction =
  (set: StoreApiSetState): CartDataActions["addMultipleToCart"] =>
  (item, quantity) => {
    if (!item.purchasable || quantity <= 0) {
      return;
    }

    set((state): CartDataState => {
      const { isWeightProductQuantityStepEnabled } = getAppConfig();
      const newProductsMap: CartDataState["cartProductsMap"] = {
        ...state.cartProductsMap,
      };

      if (item.stockQuantity < quantity) {
        toastController
          .create({
            message: i18n.t("Product Quantity is Limited!"),
            duration: 800,
          })
          .then((toast) => toast.present());
        return state;
      }

      const currentQuantity = newProductsMap[item.id]?.quantity || 0;
      const stepQuantity = getProductQuantityStep({
        product: item,
      });
      const addedQuantity =
        isWeightProductQuantityStepEnabled && isWeightBasedProduct(item) ? quantity * 0.1 : quantity;
      const newQuantity = roundToStep(currentQuantity + addedQuantity, stepQuantity);

      if (newQuantity > item.stockQuantity) {
        toastController
          .create({
            message: i18n.t("Product Quantity is Limited!"),
            duration: 800,
          })
          .then((toast) => toast.present());
        return state;
      }

      let roundedSubTotalValue = state.cartSubtotalValue;

      if (newProductsMap[item.id]) {
        const oldPrice = parseFloat(newProductsMap[item.id].price);
        const oldQuantity = newProductsMap[item.id].quantity;
        const oldTotal = oldPrice * oldQuantity;

        newProductsMap[item.id].quantity = newQuantity;
        newProductsMap[item.id].originalQuantity = newQuantity;

        if (item.selectedAttribute) {
          newProductsMap[item.id].selectedAttribute = item.selectedAttribute;
          newProductsMap[item.id].price = getEffectivePrice(item);
        }

        const newPrice = parseFloat(newProductsMap[item.id].price);
        const newTotal = newPrice * newQuantity;
        roundedSubTotalValue = Math.round((state.cartSubtotalValue - oldTotal + newTotal) * 100) / 100;
      } else {
        newProductsMap[item.id] = {
          ...createCartProductItem(item),
          quantity,
          originalQuantity: quantity,
        };

        const effectivePrice = getEffectivePrice(item);
        roundedSubTotalValue =
          Math.round((state.cartSubtotalValue + parseFloat(effectivePrice) * quantity) * 100) / 100;
      }

      AppMetricaProvider.reportEvent("addMultipleProductsToCart", {
        ...newProductsMap[item.id],
        addedQuantity: quantity,
      });

      const updatedAt = new Date();

      return {
        ...state,
        cartSubtotalValue: roundedSubTotalValue,
        cartProducts: Object.values(newProductsMap),
        cartProductsMap: newProductsMap,
        updatedAt,
      };
    });
  };

export const setProductQuantityAction =
  (set: StoreApiSetState): CartDataActions["setProductQuantity"] =>
  (productId, targetQuantity, item, isAuthenticated) => {
    if (!item.purchasable || targetQuantity < 0) {
      return;
    }
    const safeQuantity = normalizeProductQuantity({
      quantity: targetQuantity,
      product: item,
    });

    set((state): CartDataState => {
      const newProductsMap: CartDataState["cartProductsMap"] = {
        ...state.cartProductsMap,
      };

      const currentProduct = newProductsMap[productId];
      const currentQuantity = currentProduct?.quantity || 0;
      let roundedSubTotalValue = state.cartSubtotalValue;

      if (safeQuantity === 0) {
        if (currentProduct) {
          const priceToRemove = parseFloat(currentProduct.price) * currentQuantity;
          roundedSubTotalValue = Math.round((state.cartSubtotalValue - priceToRemove) * 100) / 100;

          delete newProductsMap[productId];

          AppMetricaProvider.reportEvent("removeAllProductFromCart", { id: productId });

          const isCartEmpty = Object.keys(newProductsMap).length === 0;
          const updatedAt = isCartEmpty && isAuthenticated ? new Date() : getMonthOffsetDate();

          return {
            ...state,
            cartSubtotalValue: roundedSubTotalValue,
            cartProducts: Object.values(newProductsMap),
            cartProductsMap: newProductsMap,
            coupons: isCartEmpty ? [] : state.coupons,
            rewardPoints: isCartEmpty ? 0 : state.rewardPoints,
            updatedAt,
          };
        }
        return state;
      }

      if (safeQuantity > item.stockQuantity) {
        toastController
          .create({
            message: i18n.t("Product Quantity is Limited!"),
            duration: 800,
          })
          .then((toast) => toast.present());
        return state;
      }

      if (currentProduct) {
        const oldPrice = parseFloat(currentProduct.price);
        const oldTotal = oldPrice * currentQuantity;

        newProductsMap[productId] = {
          ...currentProduct,
          quantity: safeQuantity,
          originalQuantity: safeQuantity,
        };

        if (item.selectedAttribute) {
          newProductsMap[productId].selectedAttribute = item.selectedAttribute;
          newProductsMap[productId].price = getEffectivePrice(item);
        }

        const newPrice = parseFloat(newProductsMap[productId].price);
        const newTotal = newPrice * safeQuantity;

        roundedSubTotalValue = Math.round((state.cartSubtotalValue - oldTotal + newTotal) * 100) / 100;
      } else {
        const effectivePrice = getEffectivePrice(item);
        newProductsMap[productId] = {
          ...createCartProductItem(item),
          quantity: safeQuantity,
          originalQuantity: safeQuantity,
        };

        roundedSubTotalValue =
          Math.round((state.cartSubtotalValue + parseFloat(effectivePrice) * safeQuantity) * 100) / 100;
      }

      AppMetricaProvider.reportEvent("setProductQuantity", {
        ...newProductsMap[productId],
        previousQuantity: currentQuantity,
        newQuantity: safeQuantity,
      });

      const updatedAt = new Date();

      return {
        ...state,
        cartSubtotalValue: roundedSubTotalValue,
        cartProducts: Object.values(newProductsMap),
        cartProductsMap: newProductsMap,
        updatedAt,
      };
    });
  };
